/* eslint-disable @typescript-eslint/prefer-optional-chain */
// istanbul ignore file
import React, { useEffect } from 'react'
import { CircularProgress, styled } from '@mui/material'
import type dayjs from 'dayjs'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { DatePicker } from '@mui/x-date-pickers'
import AuthError from '../../../components/ErrorComponents/AuthError'
import { type ReportData, getReactApexChartBar } from './KpiComponents/BarCharts'
import { type ConnectedProps, connect, useSelector } from 'react-redux'
import { type RootState } from '../../../store'
import { fetchDonutKpiBarGraph, fetchDonutKpiHistoryTable } from '../redux/actionCreators'
import { type ToggleOption } from '../../../components/GenericToggleButton'
import { kpiColorMap } from './KpiComponents/BuildKpiData'
import { buildDonutKpiHistoryBarGraphData, type GeneratedData } from './KpiComponents/BuildKpiHistory'
import { type KpiKeyDonut, type KpiKey, isDonutKpiWihtBar } from '../redux/changeKpiType'
import { getDonutType, getKpiService } from './KpiComponents/kpiUtils'
import HistoryDonutChart from './HistoryDonutChart'
import { type DonutKpiBarGraphState } from '../redux/historyDonutKpiBarGraphSlice'
import { type DonutKpiState } from '../types'

export interface KpiSeriesData {
  name: string
  data: number[]
}

export type KpiDispatchLoader = (startDate: string, endDate: string, kpiType: string, barType: string, service: string) => void

export const getKpiType = (kpiType: KpiKey): string => {
  switch (kpiType) {
    case 'Total Registered':
      return 'total-registered'
    case 'Total Connected':
      return 'total-connected'
    case 'Total Shipped':
      return 'total-shipped'
    case 'Total Stale':
      return 'total-stale'
    case 'Total Reads':
      return 'total-reads-in-range'
    case 'EC Mode':
      return 'ec-mode-in-range'
    case 'Signal Breakdown':
      return 'signal-strength-in-range'
    case 'Alarm Breakdown':
      return 'last-read-alarms'
    case 'Battery Breakdown':
      return 'battery-percent-in-range'
    case 'Lifecycle Breakdown':
      return 'asset-lifecycle-in-range'
    default:
      return 'total-shipped'
  }
}

export interface KpiHistoryState <T> {
  isLoading: boolean
  error: boolean
  data: T[] | undefined
  httpStatus: number
}

interface ParentProps {
  startDate: dayjs.Dayjs
  endDate: dayjs.Dayjs
  setStartDate: React.Dispatch<React.SetStateAction<dayjs.Dayjs>>
  setEndDate: React.Dispatch<React.SetStateAction<dayjs.Dayjs>>
  selectedBar: ToggleOption
  setSelectedBar: React.Dispatch<React.SetStateAction<ToggleOption>>
  selectedDonut: number
  setSelectedDonut: React.Dispatch<React.SetStateAction<number>>
  setSelectedDate: React.Dispatch<React.SetStateAction<dayjs.Dayjs>>
}

const WhiteDatePicker = styled(DatePicker)({
  '& input': {
    color: '#D0D0D0',
    fontSize: '14px',
    paddingRight: 0
  }
})

const CustomLocalizationProviderWrapper = styled('div')({
  backgroundColor: '#505050',
  border: 'none',
  borderRadius: '4px'
})

export const createReportDataTemp = (kpiName: string, selectedBar: string, kpiData: () => GeneratedData): ReportData => {
  const series = kpiData().seriesData
  let colors
  if (kpiName === 'Total Shipped') {
    colors = kpiColorMap['Total Shipped']
  } else if (kpiName === 'Total Stale') {
    colors = kpiColorMap['Total Stale']
  } else {
    if (kpiName === 'Total Registered') {
      colors = [kpiColorMap['Total Registered'][0], kpiColorMap['Total Registered'][1]]
    } else {
      colors = [kpiColorMap['Total Connected'][0], kpiColorMap['Total Connected'][1]]
    }
  }

  const categories = kpiData().category
  return {
    name: kpiName,
    type: 'bar',
    categories,
    series,
    colors,
    suffix: ''
  }
}

function HistoryDonutKpiBarGraph (props: AllProps): JSX.Element {
  const initialReportData: ReportData = {
    name: '',
    type: 'bar',
    categories: [],
    series: [],
    colors: [],
    suffix: ''
  }
  const [reportData, setReportData] = React.useState<ReportData>(initialReportData)
  const selectedKpi = useSelector((state: RootState) => state.kpiType)
  const [count, setCount] = React.useState<number>(0)

  function kpiData (): GeneratedData {
    return buildDonutKpiHistoryBarGraphData(props.donutKpiBarGraphData)
  }

  const kpiLoader = (): KpiDispatchLoader => {
    return props.fetchDonutKpiBarGraph
  }

  const state = (): DonutKpiBarGraphState => {
    return props.donutKpiBarGraphData
  }

  const loadKpiData = (): void => {
    const loader = kpiLoader()
    loader(props.startDate.format('YYYY-MM-DD'), props.endDate.format('YYYY-MM-DD'), getKpiType(selectedKpi), getDonutType(selectedKpi as KpiKeyDonut, props.selectedDonut, props.historyDonutState), getKpiService(selectedKpi))
  }

  useEffect(() => {
    if (isDonutKpiWihtBar(selectedKpi as KpiKeyDonut)) {
      loadKpiData()
    }
  }, [props.startDate, props.endDate, props.selectedDonut])

  useEffect(() => {
    setReportData(createReportDataTemp(selectedKpi, props.selectedBar.value, kpiData))
  }, [state().data])

  useEffect(() => {
    if (props.historyDonutState.data !== undefined) {
      setCount(props.historyDonutState.data.totalCount)
    }
  }, [props.historyDonutState])

  const handleGraphWithFailure = (): JSX.Element => {
    const kpiState = state()
    if (kpiState.isLoading) {
      return (
        <div style={{ width: '100%', height: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}><CircularProgress /></div>
      )
    } else if (kpiState.httpStatus === 401) {
      return (
        <div className='authErrorThirtyDaysTend daystrend401'><AuthError errorMessage="Unauthorized"/></div>
      )
    } else if (kpiState.httpStatus === 403) {
      return (<div className='authErrorThirtyDaysTend daystrend401'><AuthError errorMessage="accessForbidden"/></div>)
    } else if (kpiState.httpStatus === 200 && kpiState.data === undefined) {
      return (
        <div className='authErrorThirtyDaysTend daystrend401'><AuthError errorMessage="NoDataPresent"/></div>
      )
    } else if (kpiState.data !== undefined && kpiState.httpStatus === 200) {
      return (
        getReactApexChartBar(reportData, '300px', true, props.setSelectedDate)
      )
    } else {
      return (
        <div className='authErrorThirtyDaysTend daystrend401'><AuthError errorMessage="cannotFetch" retry={loadKpiData}/></div>
      )
    }
  }

  const handleDateChange = (type: string, date: dayjs.Dayjs): void => {
    if (type === 'start') {
      if (date.isAfter(props.endDate) || date.isSame(props.endDate)) {
        props.setStartDate(props.endDate)
      } else {
        props.setStartDate(date)
      }
    } else {
      if (date.isBefore(props.startDate) || date.isSame(props.startDate)) {
        props.setEndDate(props.startDate)
      } else {
        props.setEndDate(date)
      }
    }
  }

  return (
    <div className='kpiHistoryContainer' style={{ width: '100%' }}>
      <div className='kpiHistoryHeader'>
        <span style={{ fontSize: 18 }}>{selectedKpi} ({ count })</span>
        {
          isDonutKpiWihtBar(selectedKpi as KpiKeyDonut)
            ? (
            <div style={{ display: 'flex' }}>
              <span style={{ paddingRight: '1em', display: 'flex', alignItems: 'center' }}>
                <span style={{ paddingRight: '6px', fontSize: '14px' }}>
                Start Date
                </span>
                <span className='datePicker-deviceDetails'>
                  <CustomLocalizationProviderWrapper>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <WhiteDatePicker slotProps={{ textField: { size: 'small' } }} value={props.startDate} onAccept={(val: any) => { handleDateChange('start', val) }} />
                    </LocalizationProvider>
                  </CustomLocalizationProviderWrapper>
                </span>
              </span>
              <span style={{ paddingRight: '1em', display: 'flex', alignItems: 'center' }}>
                <span style={{ paddingRight: '6px', fontSize: '14px' }}>
                End Date
                </span>
                <span className='datePicker-deviceDetails'>
                  <CustomLocalizationProviderWrapper>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <WhiteDatePicker slotProps={{ textField: { size: 'small' } }} value={props.endDate} onAccept={(val: any) => { handleDateChange('end', val) }} />
                    </LocalizationProvider>
                  </CustomLocalizationProviderWrapper>
                </span>
              </span>
            </div>
              )
            : <></>
        }
      </div>
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <div style={{ width: '35%' }}>
          <HistoryDonutChart setSelectedDonut={props.setSelectedDonut} />
        </div>
        {
          isDonutKpiWihtBar(selectedKpi as KpiKeyDonut)
            ? (
            <div style={{ width: '65%', borderLeft: '1px solid #222222' }}>
              {handleGraphWithFailure()}
            </div>
              )
            : <></>
        }
      </div>
    </div>
  )
}

interface DispatchToProps {
  fetchDonutKpiHistoryTable: (kpiType: string, inputDateRead: string, donutType: string, page: number, size: number, service: string) => void
  fetchDonutKpiBarGraph: (startDate: string, endDate: string, kpiType: string, barType: string, service: string) => void
}

const mapDispatchToProps = (dispatch: any): DispatchToProps => ({
  fetchDonutKpiHistoryTable: (kpiType: string, inputDateRead: string, donutType: string, page: number, size: number, service: string) => dispatch(fetchDonutKpiHistoryTable(kpiType, inputDateRead, donutType, page, size, service)),
  fetchDonutKpiBarGraph: (startDate: string, endDate: string, kpiType: string, barType: string, service: string) => dispatch(fetchDonutKpiBarGraph(startDate, endDate, kpiType, barType, service))
})
interface StateToProps {
  donutKpiBarGraphData: DonutKpiBarGraphState
  historyDonutState: DonutKpiState
}

const mapStateToProps = (state: RootState): StateToProps => ({
  donutKpiBarGraphData: state.donutKpiBarGraphData,
  historyDonutState: state.historyDonutState
})

const connector = connect(mapStateToProps, mapDispatchToProps)
type PropsFromRedux = ConnectedProps<typeof connector>
type AllProps = ParentProps & PropsFromRedux
export default connector(HistoryDonutKpiBarGraph)
